import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminStore } from '@/stores/adminStore';
import { Spinner } from '@/components/common/Spinner';
import * as adminApi from '@/api/admin';

interface AdminGuardProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
}

export function AdminGuard({
  children,
  requireSuperAdmin = false,
}: AdminGuardProps) {
  const { admin, isAuthenticated, isLoading, setAdmin, clearAdmin } =
    useAdminStore();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated && isLoading) {
      adminApi
        .me()
        .then((info) => setAdmin(info))
        .catch(() => clearAdmin());
    }
  }, [isAuthenticated, isLoading, setAdmin, clearAdmin]);

  if (isLoading) {
    return <Spinner size="lg" centered />;
  }

  if (!isAuthenticated) {
    return (
      <Navigate to="/admin/login" state={{ from: location }} replace />
    );
  }

  if (requireSuperAdmin && admin?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-md-cream flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-md-graphite mb-2">
            Access Denied
          </h1>
          <p className="text-md-slate">
            This page requires super admin privileges.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
